import { useEffect, useState } from "react";

// Non-critical data (news, trending, radio) waits until the app has painted: window load AND the catalogue ready, then an idle slot (capped so slow devices still get it).
let idle = false, loaded = typeof document !== "undefined" && document.readyState === "complete", ready = false;
const waiters = new Set();
const settle = () => { idle = true; waiters.forEach((w) => w()); waiters.clear(); };
const maybeSchedule = () => {
  if (idle || !loaded || !ready) return;
  const run = () => (window.requestIdleCallback ? window.requestIdleCallback(settle, { timeout: 2500 }) : setTimeout(settle, 1200));
  requestAnimationFrame(() => setTimeout(run, 250));
};
if (typeof window !== "undefined" && !loaded) window.addEventListener("load", () => { loaded = true; maybeSchedule(); }, { once: true });

// Called once the destination catalogue has rendered (lib/destinations.jsx).
export function appReady() { ready = true; maybeSchedule(); }

export function useIdle() {
  const [ok, setOk] = useState(idle);
  useEffect(() => {
    if (idle) { setOk(true); return; }
    const w = () => setOk(true);
    waiters.add(w);
    return () => waiters.delete(w);
  }, []);
  return ok;
}

// True after the visitor's first interaction with the page (scroll, touch, key or pointer press); never fires on its own.
let interacted = false;
const interactionWaiters = new Set();
const INTERACTION_EVENTS = ["scroll", "pointerdown", "touchstart", "keydown", "wheel"];
function markInteracted() { if (interacted) return; interacted = true; INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, markInteracted)); interactionWaiters.forEach((w) => w()); interactionWaiters.clear(); }
if (typeof window !== "undefined") INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, markInteracted, { passive: true, once: false }));
export const interactNow = markInteracted;
export function useFirstInteraction() {
  const [ok, setOk] = useState(interacted);
  useEffect(() => {
    if (interacted) { setOk(true); return; }
    const w = () => setOk(true);
    interactionWaiters.add(w);
    return () => interactionWaiters.delete(w);
  }, []);
  return ok;
}

// True once the element is within `margin` of the viewport (then stays true).
export function useNearViewport(ref, margin = "600px") {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    if (!("IntersectionObserver" in window)) { setNear(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); } }, { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, margin, near]);
  return near;
}

// De-duplicates identical GET requests fired by several components in the same window (e.g. the hero and the intent strip both reading the news feed).
const inflight = new Map();
export function sharedJson(url, ttl = 60000) {
  const hit = inflight.get(url);
  if (hit && hit.at + ttl > Date.now()) return hit.p;
  const p = fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));
  inflight.set(url, { at: Date.now(), p });
  p.catch(() => inflight.delete(url));
  return p;
}
